"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { motion } from "framer-motion";
import Image from "next/image";
import { Menu, X, Send } from "lucide-react";
import { useState } from "react";
import clsx from "clsx";

const navLinks = [
  { name: "首页", path: "/" },
  { name: "技术与架构", path: "/technology" },
  { name: "API服务", path: "/api-services" },
  { name: "开发者博客", path: "/blog" },
];

export function Navbar() {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <nav className="fixed top-0 w-full z-50 glass-panel border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center gap-2">
            <Link href="/" className="flex items-center gap-2 group drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
              <div className="w-8 h-8 relative rounded overflow-hidden">
                <Image src="/images/logo.png" alt="Logo" fill className="object-cover" unoptimized />
              </div>
              <span className="font-sans font-bold text-xl tracking-wider text-white">
                旺旺助手<span className="text-neon-cyan">2.0</span>
              </span>
            </Link>
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-8">
              {navLinks.map((link) => {
                const isActive = pathname === link.path;
                return (
                  <Link
                    key={link.name}
                    href={link.path}
                    className={clsx(
                      "relative px-3 py-2 text-sm font-bold transition-colors hover:text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]",
                      isActive ? "text-neon-cyan" : "text-gray-200"
                    )}
                  >
                    {link.name}
                    {isActive && (
                      <motion.div
                        layoutId="navbar-indicator"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-neon-cyan shadow-[0_0_8px_var(--color-neon-cyan)]"
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                      />
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
          
          <div className="hidden md:flex items-center gap-4">
            <a
              href="https://t.me/fade546"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-neon-blue/20 border border-neon-blue text-white hover:bg-neon-blue hover:text-black transition-all duration-300 shadow-[0_0_15px_rgba(0,102,255,0.3)] hover:shadow-[0_0_25px_rgba(0,102,255,0.6)] font-bold rounded-sm text-sm"
            >
              <Send className="w-4 h-4" />
              @fade546
            </a>
            <Link
              href="/contact"
              className="px-6 py-2 border border-neon-cyan text-neon-cyan hover:bg-neon-cyan hover:text-black transition-all duration-300 shadow-[0_0_15px_rgba(0,243,255,0.3)] hover:shadow-[0_0_25px_rgba(0,243,255,0.6)] font-medium rounded-sm"
            >
              获取接入权限
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-400 hover:text-white p-2"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden glass-panel border-t border-white/10"
        >
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                href={link.path}
                className="text-gray-300 hover:text-neon-cyan block px-3 py-2 rounded-md text-base font-medium"
                onClick={() => setIsOpen(false)}
              >
                {link.name}
              </Link>
            ))}
          </div>
        </motion.div>
      )}
    </nav>
  );
}
